import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import LoadingSpinner from '../commonUI/LoadingSpinner';
import { getHeatmapColorsDistributionByRawData } from '../map/mapUtils';
import useScanData from '../scanDataApi/hooks/useScanData';
import { useChartFeatureMeasureLabels } from './hooks/useChartLabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

ChartJS.defaults.font.size = 16;
ChartJS.defaults.borderColor = '#7c7e80'; // darkTheme.grey.muted (set outside of theme context here)

interface Props {
  plugins?: object;
}

const DistributionChart: React.FC<Props> = ({ plugins }) => {
  const theme = useTheme();
  const { standardDeviation, mean, distributionInterval } = useScanData();
  const labelResult = useChartFeatureMeasureLabels();
  const chartRef = React.useRef<ChartJS<'bar', number[], unknown> | null>(null);

  const isLoading =
    standardDeviation.isLoading ||
    mean.isLoading ||
    distributionInterval.isLoading;

  const ranges = !(standardDeviation.data && mean.data)
    ? []
    : getHeatmapColorsDistributionByRawData(standardDeviation.data, mean.data);

  const datasets = [
    {
      label: 'interval',
      data:
        distributionInterval?.data?.map(({ interval_value }) =>
          Number(interval_value)
        ) || [],
      fill: false,
      backgroundColor:
        distributionInterval?.data?.map(
          ({ interval }) =>
            ranges.find(
              (range) => range[0] <= interval && range[1] >= interval
            )?.[2] || theme.colors.common.purple
        ) || theme.colors.common.purple,
    },
  ];

  const data = {
    datasets,
    labels: distributionInterval?.data?.map(({ interval }) => interval),
    rawData: distributionInterval,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },

      ...plugins,
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        ticks: {
          color: theme.colors.common.white,
        },
        grid: {
          display: true,
          color: theme.colors.darkTheme.grey.muted,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.yLabel : '',
          color: theme.colors.common.white,
        },
      },
      x: {
        ticks: {
          color: theme.colors.common.white,
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.xLabel : '',
          color: theme.colors.common.white,
        },
      },
    },
    events: ['click'] as Array<keyof HTMLElementEventMap>,
  };

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {!isLoading && data && (
        <Bar
          ref={chartRef}
          data={data}
          options={options}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            minWidth: '300px',
            position: 'absolute', // this is needed to make the chart responsive - https://github.com/chartjs/Chart.js/issues/9001
          }}
        />
      )}
    </>
  );
};

export default DistributionChart;
